import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, X, Timer, Sparkles, BookOpen, ClipboardList, FilePenLine, ScrollText } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faXmark, faFingerprint } from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { MapContainer, TileLayer, Marker, Popup as MapPopup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  ReferenceLine,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

import Filter from "./AttendanceLogFilter";
import Pagination from "../../../components/common/Pagination";
import ActiveCheckinList from "./ActiveCheckinList";
import AttendanceLogCreateModal from "./AttendanceLogCreateModal";
import AttendanceLogTable from "./AttendanceLogTable";
import { formatDateVN } from "../../../utils/formatUtils";

import {
  getAllAttendanceLog,
  getActiveCheckins,
  checkoutAttendance,
  exportAttendanceLogs,
  getAttendanceStatus,
} from "../../../api/hrm/employees/attendanceLog";

import {
  showConfirmDialog,
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";

import Spinner from "../../../components/ui/Spinner";
import { buildPivotAoA, parseMonthKey } from "./attendanceExportPivot";
import { downloadAttendanceMatrixExcel } from "./attendanceExportExcel";
import { startAttendanceLogListTour } from "./attendanceLogListTour";
import { DEFAULT_MAP_CENTER, parseLatLng } from "./attendanceLocationUtils";
import AttendanceConfirmationTab from "./AttendanceConfirmationTab";
import AttendanceConfirmationActivityTab from "./AttendanceConfirmationActivityTab";
import { hasPermission } from "../../../utils/permissionUtils";

// 🟢 icon marker
const greenIcon = new L.Icon({
  iconUrl: "https://maps.gstatic.com/mapfiles/ms2/micons/green-dot.png",
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});
const redIcon = new L.Icon({
  iconUrl: "https://maps.gstatic.com/mapfiles/ms2/micons/red-dot.png",
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

const ATTENDANCE_MAIN_TABS = [
  {
    id: "logs",
    label: "Phiếu chấm công",
    shortLabel: "Phiếu công",
    Icon: ClipboardList,
  },
  {
    id: "confirmation",
    label: "Xác nhận / Giải trình giờ công",
    shortLabel: "Xác nhận / GT",
    Icon: FilePenLine,
    requireConfirm: true,
  },
  {
    id: "activity",
    label: "Nhật ký xác nhận / giải trình",
    shortLabel: "Nhật ký",
    Icon: ScrollText,
    requireConfirm: true,
  },
];

const AttendanceLogListPage = () => {
  const [filters, setFilters] = useState({});
  const [activeCheckins, setActiveCheckins] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attendanceList, setAttendanceList] = useState([]);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [dailyWorkChart, setDailyWorkChart] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartMetric, setChartMetric] = useState("people");
  const [selectedChartDay, setSelectedChartDay] = useState(null);
  const [mainTab, setMainTab] = useState("logs");
  const mobileTabsRef = useRef(null);
  const canAttendanceConfirm = hasPermission("hrm.attendance.confirm", "R");

  const visibleMainTabs = useMemo(
    () =>
      ATTENDANCE_MAIN_TABS.filter((tab) => !tab.requireConfirm || canAttendanceConfirm),
    [canAttendanceConfirm]
  );

  useEffect(() => {
    const activeBtn = mobileTabsRef.current?.querySelector(
      `[data-attendance-tab="${mainTab}"]`
    );
    activeBtn?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [mainTab]);

  //--------------------------------------------------------------------
  // 📌 FETCH DỮ LIỆU
  //--------------------------------------------------------------------
  const buildTableFilters = (baseFilters = filters, dayOverride = selectedChartDay) => {
    if (!dayOverride) return baseFilters;
    return {
      ...baseFilters,
      from_date: dayOverride,
      to_date: dayOverride,
    };
  };

  const fetchData = async (page, size, dayOverride = selectedChartDay) => {
    setLoading(true);
    try {
      const res = await getAllAttendanceLog({
        ...buildTableFilters(filters, dayOverride),
        page,
        size,
      });
      setAttendanceList(res.records || []);
      setTotalRecords(res.total || 0);
    } catch (err) {
      console.error("❌ Lỗi load dữ liệu:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceStatus();
  }, []);

  useEffect(() => {
    if (
      (mainTab === "confirmation" || mainTab === "activity") &&
      !hasPermission("hrm.attendance.confirm", "R")
    ) {
      setMainTab("logs");
    }
  }, [mainTab]);

  const fetchActiveCheckinsHandler = async () => {
    try {
      const res = await getActiveCheckins();
      setActiveCheckins(res || []);
    } catch (err) {
      console.error("❌ Lỗi getActiveCheckins:", err);
    }
  };

  const fetchAttendanceStatus = async () => {
    try {
      const res = await getAttendanceStatus();
      setAttendanceStatus(res);
    } catch (err) {
      console.error("❌ Lỗi lấy trạng thái chấm công:", err);
    }
  };

  const normalizeDateKey = (value) => {
    if (!value) return "";
    const s = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const buildRangeDays = (fromDate, toDate) => {
    if (!fromDate || !toDate) return [];
    const from = new Date(fromDate);
    const to = new Date(toDate);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) return [];
    const days = [];
    const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    while (cursor <= end) {
      const y = cursor.getFullYear();
      const m = String(cursor.getMonth() + 1).padStart(2, "0");
      const d = String(cursor.getDate()).padStart(2, "0");
      days.push(`${y}-${m}-${d}`);
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  };

  const fetchDailyWorkChart = async () => {
    if (!filters?.from_date || !filters?.to_date) {
      setDailyWorkChart([]);
      return;
    }
    setChartLoading(true);
    try {
      // Dữ liệu export đã được BE áp data_scope theo tài khoản đăng nhập
      const res = await exportAttendanceLogs({
        from_date: filters.from_date,
        to_date: filters.to_date,
      });
      const records = res?.records || [];

      const dayEmployeeMap = new Map();
      const dayRecordCountMap = new Map();
      records.forEach((row) => {
        const dayKey = normalizeDateKey(row.work_date);
        if (!dayKey) return;
        const employeeKey =
          row.employee_id || row.employee_code || row.employee_name || row.staff_id || "unknown";
        const set = dayEmployeeMap.get(dayKey) || new Set();
        set.add(String(employeeKey));
        dayEmployeeMap.set(dayKey, set);
        dayRecordCountMap.set(dayKey, Number(dayRecordCountMap.get(dayKey) || 0) + 1);
      });

      const dayKeys = buildRangeDays(filters.from_date, filters.to_date);
      const chartRows = dayKeys.map((dayKey) => {
        const set = dayEmployeeMap.get(dayKey);
        const totalPeople = set ? set.size : 0;
        const totalRecords = Number(dayRecordCountMap.get(dayKey) || 0);
        const dateObj = new Date(dayKey);
        const dayLabel = `${String(dateObj.getDate()).padStart(2, "0")}/${String(
          dateObj.getMonth() + 1
        ).padStart(2, "0")}`;
        const today = new Date();
        const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(today.getDate()).padStart(2, "0")}`;
        return {
          dateKey: dayKey,
          dayLabel,
          totalPeople,
          totalRecords,
          isToday: dayKey === todayKey,
        };
      });
      setDailyWorkChart(chartRows);
    } catch (err) {
      console.error("❌ Lỗi tải chart chấm công theo ngày:", err);
      setDailyWorkChart([]);
    } finally {
      setChartLoading(false);
    }
  };

  //--------------------------------------------------------------------
  // 📌 CHECKOUT
  //--------------------------------------------------------------------
  const handleCheckout = async (log, checkoutData) => {
    try {
      setLoading(true);

      await checkoutAttendance(log.id, checkoutData.check_out_location);

      const today = new Date().toISOString().slice(0, 10);

      setFilters((prev) => ({
        ...prev,
        from_date: today,
        to_date: today,
      }));

      setCurrentPage(1);

      await fetchActiveCheckinsHandler();
      await fetchData(1, pageSize);
      await fetchAttendanceStatus();

      showSuccessToast("Checkout thành công!");
    } catch (err) {
      console.error("❌ Checkout error:", err);
      showErrorToast("Checkout thất bại!");
    } finally {
      setLoading(false);
    }
  };

  //--------------------------------------------------------------------
  // 📌 XUẤT EXCEL (ma trận: cột = ngày trong tháng)
  //--------------------------------------------------------------------
  const handleExportXlsxRequest = async ({ mode, branch_id, month, branch_label }) => {
    const parsed = parseMonthKey(month);
    if (!parsed) {
      showErrorToast("Chọn tháng / năm hợp lệ.");
      return;
    }
    try {
      const res = await exportAttendanceLogs({
        branch_id: branch_id || undefined,
        from_date: parsed.from_date,
        to_date: parsed.to_date,
      });
      const records = res.records || [];
      if (records.length === 0) {
        showErrorToast("Không có dữ liệu chấm công trong khoảng đã chọn.");
        return;
      }

      const aoa = buildPivotAoA(records, parsed.year, parsed.month, mode);
      if (aoa.length <= 1) {
        showErrorToast("Không có nhân viên để kết xuất.");
        return;
      }

      const branchTag = branch_id ? String(branch_id).slice(0, 8) : "tat-ca";
      const filePrefix =
        mode === "detail"
          ? "cham-cong-chi-tiet"
          : mode === "actual_hours"
            ? "cham-cong-gio-thuc-te"
            : "cham-cong-ngay-cong";
      const filename = `${filePrefix}_${month}_${branchTag}.xlsx`;

      await downloadAttendanceMatrixExcel(aoa, {
        branchLabel: branch_label,
        year: parsed.year,
        month: parsed.month,
        mode,
        filename,
      });
      showSuccessToast("Đã tải file Excel.");
    } catch (err) {
      console.error("❌ Export xlsx:", err);
      showErrorToast("Không thể xuất Excel.");
    }
  };




  const chartRangeRef = useRef({ from_date: null, to_date: null });

  //--------------------------------------------------------------------
  // 📌 USE EFFECT - LOAD DATA
  //--------------------------------------------------------------------
  useEffect(() => {
    if (filters.from_date && filters.to_date) {
      const rangeChanged =
        chartRangeRef.current.from_date !== filters.from_date ||
        chartRangeRef.current.to_date !== filters.to_date;
      chartRangeRef.current = {
        from_date: filters.from_date,
        to_date: filters.to_date,
      };

      if (rangeChanged) {
        // Đổi tháng → bỏ lọc ngày từ biểu đồ, tải lại chart
        setSelectedChartDay(null);
        setCurrentPage(1);
        fetchData(1, pageSize, null);
        fetchDailyWorkChart();
      } else {
        // Đổi keyword / refresh → giữ lọc ngày nếu có
        setCurrentPage(1);
        fetchData(1, pageSize);
      }
      fetchActiveCheckinsHandler();
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (filters.from_date && filters.to_date) {
      fetchData(currentPage, pageSize);
    }
  }, [currentPage, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyChartDayFilter = (dayKey) => {
    if (!dayKey) return;
    const nextDay = selectedChartDay === dayKey ? null : dayKey;
    setSelectedChartDay(nextDay);
    setCurrentPage(1);
    fetchData(1, pageSize, nextDay);
  };

  const clearChartDayFilter = () => {
    if (!selectedChartDay) return;
    setSelectedChartDay(null);
    setCurrentPage(1);
    fetchData(1, pageSize, null);
  };

  const handleBarChartClick = (state) => {
    const dayKey =
      state?.activePayload?.[0]?.payload?.dateKey ||
      (state?.activeTooltipIndex != null
        ? dailyWorkChart[state.activeTooltipIndex]?.dateKey
        : null);
    applyChartDayFilter(dayKey);
  };

  //--------------------------------------------------------------------
  // 📌 FORMAT HỖ TRỢ
  //--------------------------------------------------------------------
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatWorkDuration = (duration) => {
    if (!duration) return "-";
  
    // CASE: PostgreSQL interval object {hours, minutes, seconds}
    if (typeof duration === "object") {
      const h = duration.hours ?? 0;
      const m = duration.minutes ?? 0;
      return `${h} giờ ${m} phút`;
    }
  
    // CASE: string HH:MM:SS → fallback
    if (typeof duration === "string" && duration.includes(":")) {
      const [h, m] = duration.split(":").map((n) => parseInt(n, 10));
      return `${h} giờ ${m} phút`;
    }
  
    // CASE: number total minutes
    if (typeof duration === "number") {
      const h = Math.floor(duration / 60);
      const m = duration % 60;
      return `${h} giờ ${m} phút`;
    }
  
    return "-";
  };
  

  //--------------------------------------------------------------------
  // 📌 PAGINATION
  //--------------------------------------------------------------------
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page, pageSize);   // <-- BẮT BUỘC CÓ
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  //--------------------------------------------------------------------
  // 📌 RENDER
  //--------------------------------------------------------------------
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const currentMetricKey = chartMetric === "records" ? "totalRecords" : "totalPeople";
  const currentMetricLabel =
    chartMetric === "records" ? "Số phiếu chấm công" : "Số người làm việc";
  const todayKey = new Date().toISOString().slice(0, 10);
  const averageCutoffKey = filters?.to_date
    ? filters.to_date < todayKey
      ? filters.to_date
      : todayKey
    : todayKey;
  const averageSource = dailyWorkChart.filter(
    (item) => item.dateKey >= (filters?.from_date || item.dateKey) && item.dateKey <= averageCutoffKey
  );
  const averageValue =
    averageSource.length > 0
      ? Number(
          (
            averageSource.reduce(
              (sum, item) => sum + Number(item[currentMetricKey] || 0),
              0
            ) / averageSource.length
          ).toFixed(2)
        )
      : 0;

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6 md:p-8">
      {/* HEADER */}
      <div className="mb-6 flex items-start justify-between gap-3" data-tour="hrm-attendance-header">
        <div className="flex items-center gap-4">
          <div className="bg-red-700 p-2.5 rounded-2xl text-white shadow-lg shadow-red-200">
            <FontAwesomeIcon
              icon={faFingerprint}
              className="text-xl w-6 h-6 flex items-center justify-center"
            />
          </div>

          <div>
            <h1 className="text-xl font-black text-gray-800 tracking-tight uppercase">
              Quản lý <span className="text-red-700">chấm công</span>
            </h1>

            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider text-nowrap">
                <span className="text-red-600">{activeCheckins.length}</span>{" "} nhân viên đang làm việc
              </p>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2" data-tour="hrm-attendance-header-guides">
          <button
            type="button"
            onClick={() => {
              setMainTab("logs");
              window.setTimeout(() => {
                startAttendanceLogListTour({
                  canAttendanceConfirm,
                  switchToTab: setMainTab,
                });
              }, 100);
            }}
            title="Product tour — hướng dẫn nhanh theo từng vùng màn hình"
            aria-label="Product tour — hướng dẫn nhanh"
            className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
          >
            <Sparkles className="h-5 w-5" strokeWidth={2.2} />
            <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
              Hướng dẫn nhanh
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
              window.open(`${base}/guides/hrm-attendance-full-process-guide.html`, "_blank", "noopener,noreferrer");
            }}
            title="Hướng dẫn sử dụng — quy trình đầy đủ (tab mới)"
            aria-label="Hướng dẫn sử dụng"
            className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-700 shadow-sm transition-all hover:border-red-200 hover:bg-red-50 hover:text-red-800"
          >
            <BookOpen className="h-5 w-5" strokeWidth={2.2} />
            <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-bold text-gray-800 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
              Hướng dẫn sử dụng
            </span>
          </button>
        </div>
      </div>

      <div className="mb-4 min-w-0" data-tour="hrm-attendance-tabs">
        <div className="md:hidden">
          <div className="rounded-2xl border border-gray-100 bg-white p-1 shadow-sm">
            <div
              ref={mobileTabsRef}
              role="tablist"
              aria-label="Chấm công — chọn mục"
              className="flex gap-1 overflow-x-auto scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {visibleMainTabs.map((tab) => {
                const isActive = mainTab === tab.id;
                const TabIcon = tab.Icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    data-attendance-tab={tab.id}
                    title={tab.label}
                    onClick={() => setMainTab(tab.id)}
                    className={`flex shrink-0 snap-start items-center gap-1.5 rounded-xl px-3 py-2.5 text-[11px] font-black uppercase tracking-wide whitespace-nowrap transition-all ${
                      isActive
                        ? "bg-red-700 text-white shadow-sm shadow-red-200/60"
                        : "text-gray-600 hover:bg-gray-50 active:bg-gray-100"
                    }`}
                  >
                    <TabIcon className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden />
                    {tab.shortLabel}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="hidden flex-wrap gap-2 border-b border-gray-200 pb-1 md:flex">
          {visibleMainTabs.map((tab) => {
            const isActive = mainTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setMainTab(tab.id)}
                className={`rounded-t-xl px-5 py-2.5 text-sm font-black uppercase tracking-wide transition-colors ${
                  isActive
                    ? "border border-b-0 border-gray-200 -mb-px bg-white text-red-800"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {mainTab === "activity" ? (
        canAttendanceConfirm ? (
          <AttendanceConfirmationActivityTab />
        ) : null
      ) : mainTab === "confirmation" ? (
        canAttendanceConfirm ? (
          <AttendanceConfirmationTab />
        ) : null
      ) : (
        <>
      {/* Đang checkin */}
      <div data-tour="hrm-attendance-active">
        <ActiveCheckinList
          data={activeCheckins}
          currentUserId={user?.id}
          currentUserRole={user?.role}
          onCheckout={handleCheckout}
          onUpdated={() => fetchData(currentPage, pageSize)}
        />
      </div>

      <h2 className="font-semibold text-base text-gray-700 mb-3 ml-2">
        Bảng danh sách phiếu chấm công ({totalRecords}
        {selectedChartDay ? ` — ${formatDateVN(selectedChartDay)}` : ""})
      </h2>

      {/* Bộ lọc */}
      <div data-tour="hrm-attendance-filter">
        <Filter
          onNewClick={() => setShowDrawer(true)}
          onFilterChange={setFilters}
          hasUnfinishedCheckin={activeCheckins.some((log) => log.employee_id === user?.id)}
          attendanceStatus={attendanceStatus}
          onExportXlsxRequest={handleExportXlsxRequest}
        />
      </div>

      <div className="mb-6 rounded-[2rem] border border-gray-100 bg-white p-5 shadow-sm" data-tour="hrm-attendance-chart">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-black uppercase tracking-wide text-gray-700 flex items-center gap-2">
            Biểu đồ số người làm việc theo ngày
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-600 normal-case">
              {currentMetricLabel}
            </span>
          </h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setChartMetric("people")}
              className={`rounded-lg px-2.5 py-1 text-[11px] font-bold transition-all ${
                chartMetric === "people"
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Người làm việc
            </button>
            <button
              type="button"
              onClick={() => setChartMetric("records")}
              className={`rounded-lg px-2.5 py-1 text-[11px] font-bold transition-all ${
                chartMetric === "records"
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Phiếu chấm công
            </button>
            <span className="text-[11px] font-bold text-gray-400">
              {filters?.from_date && filters?.to_date
                ? `${formatDateVN(filters.from_date)} - ${formatDateVN(filters.to_date)}`
                : "Chưa chọn tháng"}
            </span>
          </div>
        </div>
        {selectedChartDay && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2">
            <span className="text-[12px] font-bold text-blue-700">
              Đang lọc bảng chi tiết theo ngày {formatDateVN(selectedChartDay)}
            </span>
            <button
              type="button"
              onClick={clearChartDayFilter}
              className="inline-flex items-center gap-1 rounded-lg bg-white px-2 py-1 text-[11px] font-bold text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
            >
              <X size={12} strokeWidth={2.5} />
              Bỏ lọc
            </button>
            <span className="text-[11px] text-blue-500 font-medium">
              Click lại cột đã chọn để bỏ lọc
            </span>
          </div>
        )}
        <p className="mb-2 text-[11px] font-medium text-gray-400">
          Click vào cột biểu đồ để lọc danh sách phiếu theo ngày đó.
        </p>
        <div className="h-[260px] w-full">
          {chartLoading ? (
            <div className="flex h-full items-center justify-center text-sm font-semibold text-gray-400">
              Đang tải dữ liệu biểu đồ...
            </div>
          ) : dailyWorkChart.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm font-semibold text-gray-400">
              Không có dữ liệu chấm công trong tháng đã chọn.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dailyWorkChart}
                margin={{ top: 8, right: 10, left: 0, bottom: 0 }}
                onClick={handleBarChartClick}
                style={{ cursor: "pointer" }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="dayLabel"
                  tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                  axisLine={false}
                  tickLine={false}
                  width={36}
                />
                <Tooltip
                  formatter={(value, name, ctx) => {
                    const row = ctx?.payload;
                    if (!row) return [`${value}`, currentMetricLabel];
                    return [
                      `${row.totalPeople} người, ${row.totalRecords} phiếu`,
                      "Dữ liệu ngày",
                    ];
                  }}
                  labelFormatter={(label) => `Ngày ${label}`}
                  cursor={{ fill: "#eff6ff" }}
                />
                <ReferenceLine
                  y={averageValue}
                  stroke="#f59e0b"
                  strokeDasharray="6 6"
                  label={{
                    value: `TB đến hôm nay: ${averageValue}`,
                    position: "insideTopRight",
                    fill: "#b45309",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                />
                <Bar
                  dataKey={currentMetricKey}
                  name={currentMetricKey}
                  fill="#2563eb"
                  radius={[6, 6, 0, 0]}
                  maxBarSize={26}
                  cursor="pointer"
                >
                  {dailyWorkChart.map((entry) => {
                    const isSelected = selectedChartDay === entry.dateKey;
                    let fill = entry.isToday ? "#ef4444" : "#2563eb";
                    if (isSelected) fill = "#0f766e";
                    else if (selectedChartDay) fill = entry.isToday ? "#fca5a5" : "#93c5fd";
                    return (
                      <Cell
                        key={`cell-${entry.dateKey}`}
                        fill={fill}
                        stroke={isSelected ? "#115e59" : undefined}
                        strokeWidth={isSelected ? 2 : 0}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* BẢNG CHÍNH — (ĐÃ TÁCH RA FILE RIÊNG) */}
      <div data-tour="hrm-attendance-table">
        {loading ? (
          <Spinner />
        ) : (
          <AttendanceLogTable
            data={attendanceList}
            currentPage={currentPage}
            pageSize={pageSize}
            onSelectLog={setSelectedLog}
          />
        )}
      </div>

      <Pagination
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Drawer tạo mới */}
      <AttendanceLogCreateModal
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        onSaved={() => {
          fetchData(currentPage, pageSize);
          fetchActiveCheckinsHandler();
          fetchAttendanceStatus();
          setShowDrawer(false);
        }}
      />

{/* ✅ POPUP VỊ TRÍ CHẤM CÔNG NÂNG CẤP */}
<AnimatePresence>
  {selectedLog && (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      {/* Overlay mờ cao cấp */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setSelectedLog(null)}
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
      />

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 40 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="relative bg-white w-[800px] max-w-[95vw] rounded-[2.5rem] shadow-2xl overflow-hidden border border-gray-100 flex flex-col"
      >
        {/* HEADER */}
        <div className="p-8 pb-4 flex justify-between items-center bg-white">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shadow-sm border border-blue-100">
              <MapPin size={24} strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="text-xl font-black uppercase tracking-tight text-gray-800">Lộ trình chấm công</h2>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-0.5 italic">Dữ liệu GPS thời gian thực</p>
            </div>
          </div>
          <button 
            onClick={() => setSelectedLog(null)}
            className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-2xl transition-all border border-gray-100"
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        {/* 🔵 THÔNG TIN CHI TIẾT */}
        <div className="px-8 pb-6">
          <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 grid grid-cols-2 md:grid-cols-3 gap-6">
            
            {/* Cột 1: Nhân sự */}
            <div className="space-y-1">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Nhân viên</p>
              <p className="text-sm font-black text-slate-800 uppercase tracking-tight truncate">
                {selectedLog.employee_name || "-"}
              </p>
              <p className="text-[11px] font-bold text-blue-500 italic">
                {selectedLog.branch_name}
              </p>
            </div>

            {/* Cột 2: Thời gian làm việc */}
            <div className="space-y-1">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Ngày làm việc</p>
              <p className="text-sm font-black text-slate-800">
                {selectedLog.work_date ? formatDateVN(selectedLog.work_date) : "-"}
              </p>
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-100 text-[10px] font-black">
                <Timer size={10} /> {formatWorkDuration(selectedLog.work_duration)}
              </div>
            </div>

            {/* Cột 3: Chi tiết Check-in/out */}
            <div className="space-y-1">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Khung giờ</p>
              <div className="flex items-center gap-3">
                <div className="text-center">
                  <p className="text-[10px] font-bold text-emerald-500 uppercase">In</p>
                  <p className="text-sm font-black text-slate-700">{selectedLog.check_in_time?.slice(0,5) || "--:--"}</p>
                </div>
                <div className="text-slate-300">→</div>
                <div className="text-center">
                  <p className="text-[10px] font-bold text-rose-500 uppercase">Out</p>
                  <p className="text-sm font-black text-slate-700">{selectedLog.check_out_time?.slice(0,5) || "--:--"}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* MAP AREA */}
        <div className="h-[400px] w-full relative">
          {/* Overlay nhãn vị trí trên bản đồ */}
          <div className="absolute top-4 left-4 z-[500] flex flex-col gap-2">
            <div className="px-4 py-2 bg-white/90 backdrop-blur shadow-sm border border-emerald-100 rounded-xl flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-[10px] font-black uppercase text-emerald-700">Điểm vào</span>
            </div>
            <div className="px-4 py-2 bg-white/90 backdrop-blur shadow-sm border border-rose-100 rounded-xl flex items-center gap-2">
              <div className="w-2 h-2 bg-rose-500 rounded-full" />
              <span className="text-[10px] font-black uppercase text-rose-700">Điểm ra</span>
            </div>
          </div>

          <div className="h-full w-full grayscale-[0.2] contrast-[1.1]">
            {(() => {
              const inLoc = parseLatLng(selectedLog.check_in_location);
              const outLoc = parseLatLng(selectedLog.check_out_location);
              const center = inLoc || outLoc || DEFAULT_MAP_CENTER;
              const line = inLoc && outLoc ? [inLoc, outLoc] : null;
              const hasGps = Boolean(inLoc || outLoc);

              if (!hasGps) {
                return (
                  <div className="flex h-full items-center justify-center bg-slate-50 text-sm font-semibold text-gray-500">
                    Không có tọa độ GPS hợp lệ cho phiếu này.
                  </div>
                );
              }

              return (
                <MapContainer
                  key={selectedLog.id}
                  center={center}
                  zoom={15}
                  scrollWheelZoom={true}
                  className="h-full w-full"
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  {inLoc && (
                    <Marker position={inLoc} icon={greenIcon}>
                      <MapPopup className="custom-map-popup">
                        <div className="font-black text-emerald-600">CHECK-IN: {selectedLog.check_in_time?.slice(0,5)}</div>
                      </MapPopup>
                    </Marker>
                  )}
                  {outLoc && (
                    <Marker position={outLoc} icon={redIcon}>
                      <MapPopup className="custom-map-popup">
                        <div className="font-black text-rose-600">CHECK-OUT: {selectedLog.check_out_time?.slice(0,5)}</div>
                      </MapPopup>
                    </Marker>
                  )}
                  {line && <Polyline positions={line} color="#3b82f6" weight={4} dashArray="10, 10" />}
                </MapContainer>
              );
            })()}
          </div>
        </div>
      </motion.div>
    </div>
  )}
</AnimatePresence>
        </>
      )}

    </div>
  );
};

export default AttendanceLogListPage;
